import { useState } from 'react';
import { ConfirmDialog } from '@/components/shared';
import { SeloGuardiao } from '../../guardioes/SeloGuardiao';
import { useAuth } from '@/contexts/AuthContext';
import { useComentariosPublicacaoQuery, usePodePublicar, usePublicacaoLeitorMutations } from '../usePublicacoes';
import { millis } from '../publicacao.definitions';
import type { ComentarioPublicacao, SeloComentario } from '../publicacao.types';

const SELOS: Record<SeloComentario, { texto: string; selo: string; fundo: string }> = {
  si: { texto: 'SI', selo: 'bg-primary-100 text-primary-700', fundo: 'border-primary-100 bg-primary-50/50' },
  comunicacao: { texto: 'Comunicação', selo: 'bg-sky-100 text-sky-700', fundo: 'border-sky-100 bg-sky-50/50' },
};

function iniciais(nome: string): string {
  return nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

function Avatar({ comentario }: { comentario: ComentarioPublicacao }) {
  if (comentario.autor?.avatar) {
    return <img src={comentario.autor.avatar} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />;
  }
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
      {iniciais(comentario.autor?.name ?? '?')}
    </span>
  );
}

/** Comentários do post: autor edita/exclui o próprio; quem publica também exclui (moderação). */
export function ComentariosPublicacao({ publicacaoId }: { publicacaoId: string }) {
  const { user } = useAuth();
  const podePublicar = usePodePublicar();
  const { data: comentarios = [], isLoading } = useComentariosPublicacaoQuery(publicacaoId);
  const { comentar, editarComentario, excluirComentario } = usePublicacaoLeitorMutations(publicacaoId);
  const [texto, setTexto] = useState('');
  const [editando, setEditando] = useState<{ id: string; texto: string } | null>(null);
  const [excluindoId, setExcluindoId] = useState<string | null>(null);

  const enviar = () => {
    if (!texto.trim()) return;
    comentar.mutate(texto, { onSuccess: () => setTexto('') });
  };

  return (
    <section className="space-y-3">
      <h3 className="font-montserrat text-base font-semibold text-raro-gray-900">
        Comentários {comentarios.length > 0 && <span className="text-xs font-normal text-raro-gray-500">({comentarios.length})</span>}
      </h3>

      <div className="rounded-2xl border border-raro-gray-200 bg-white p-4 shadow-sm">
        <textarea
          className="input-primary w-full"
          rows={2}
          maxLength={5000}
          placeholder="Escreva um comentário..."
          value={texto}
          onChange={(event) => setTexto(event.target.value)}
        />
        <div className="mt-2 flex justify-end">
          <button type="button" className="btn-primary text-sm" disabled={!texto.trim() || comentar.isPending} onClick={enviar}>
            {comentar.isPending ? 'Enviando...' : 'Comentar'}
          </button>
        </div>
      </div>

      {isLoading && <div className="h-16 animate-pulse rounded-2xl bg-raro-gray-100" />}

      <ul className="space-y-3">
        {comentarios.map((comentario) => {
          const meu = comentario.autorEmail === user?.email;
          const criado = millis(comentario.criadoEm);
          return (
            <li
              key={comentario.id}
              className={`flex gap-3 rounded-2xl border p-4 ${
                comentario.autorSelo ? SELOS[comentario.autorSelo].fundo : 'border-raro-gray-200 bg-white'
              }`}
            >
              <Avatar comentario={comentario} />
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <b className="text-raro-gray-900">{comentario.autor?.name}</b>
                  <SeloGuardiao email={comentario.autorEmail} colaboradorId={comentario.autor?.id} />
                  {comentario.autorSelo && (
                    <span className={`ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${SELOS[comentario.autorSelo].selo}`}>
                      {SELOS[comentario.autorSelo].texto}
                    </span>
                  )}
                  <span className="text-xs text-raro-gray-400">
                    {criado ? ` · ${new Date(criado).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                    {comentario.editadoEm ? ' · editado' : ''}
                  </span>
                </p>
                {editando?.id === comentario.id ? (
                  <div className="mt-1 space-y-2">
                    <textarea
                      className="input-primary w-full"
                      rows={2}
                      maxLength={5000}
                      value={editando.texto}
                      onChange={(event) => setEditando({ id: comentario.id, texto: event.target.value })}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-primary text-xs"
                        disabled={!editando.texto.trim()}
                        onClick={() =>
                          editarComentario.mutate(
                            { comentarioId: comentario.id, texto: editando.texto },
                            { onSuccess: () => setEditando(null) }
                          )
                        }
                      >
                        Salvar
                      </button>
                      <button type="button" className="btn-secondary text-xs" onClick={() => setEditando(null)}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-raro-gray-700">{comentario.texto}</p>
                )}
                {(meu || podePublicar) && editando?.id !== comentario.id && (
                  <div className="mt-2 flex gap-3 text-xs font-semibold">
                    {meu && (
                      <button
                        type="button"
                        className="text-raro-gray-500 hover:text-primary-700"
                        onClick={() => setEditando({ id: comentario.id, texto: comentario.texto })}
                      >
                        Editar
                      </button>
                    )}
                    <button
                      type="button"
                      className="text-raro-gray-500 hover:text-rose-600"
                      onClick={() => setExcluindoId(comentario.id)}
                    >
                      Excluir
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        isOpen={excluindoId != null}
        title="Excluir comentário"
        message="O comentário será apagado e não poderá ser recuperado. Deseja continuar?"
        type="danger"
        confirmText="Excluir"
        confirmando={excluirComentario.isPending}
        onCancel={() => setExcluindoId(null)}
        onConfirm={() => {
          if (excluindoId) excluirComentario.mutate(excluindoId, { onSettled: () => setExcluindoId(null) });
        }}
      />
    </section>
  );
}
